import { AbsenceDbModel } from '@db-models/absence.model';
import { AvailabilityDbModel } from '@db-models/availability-db.model';
import { StoreDbModel } from '@db-models/store-db.model';
import { StoreWorkerAssignment } from '@db-models/stores-worker.model';
import { WorkerDbModel } from '@db-models/worker-db.model';
import { StoreScopedWorker } from '@db-models/store-scope.model';

/**
 * The set of store ids a worker serves. Multi-store aware: uses the served
 * `stores[]` when the backend provides them, else falls back to the scalar
 * `store_id` (addon-off → byte-for-byte the old single-store behaviour).
 */
function servedStoreIds(worker: StoreScopedWorker | null | undefined): (number | null | undefined)[] {
  if (worker?.stores?.length) {
    return worker.stores.map(store => store.id);
  }
  return [worker?.store_id];
}

/**
 * True when two workers share at least one store — the multi-store replacement
 * for `a.store_id === b.store_id`. Addon-off reduces to that exact equality.
 */
export function workersShareStore(a: StoreScopedWorker, b: StoreScopedWorker): boolean {
  const aIds = servedStoreIds(a).filter(id => id != null);
  const bIds = servedStoreIds(b).filter(id => id != null);
  // two store-less workers matched under the legacy scalar compare (null === null), and the list
  // filters still rely on it: without it, own-store-permission roles get EMPTY worker lists at
  // store-less partners. This is deliberately WIDER than cakephp, whose workersShareStore() never
  // lets two different store-less workers share — an action gate built on it can show a control
  // the API then refuses
  if (!aIds.length && !bIds.length) {
    return true;
  }
  return aIds.some(id => bIds.includes(id));
}

/**
 * True when a worker serves the given store — the multi-store replacement for
 * `worker.store_id === storeId`.
 */
export function workerServesStore(worker: StoreScopedWorker, storeId: number | null | undefined): boolean {
  if (storeId == null) {
    return false;
  }
  return servedStoreIds(worker).includes(storeId);
}

/** True when a worker has no store assignment — never excluded by a store filter. */
export function workerHasNoStore(worker: WorkerDbModel | null | undefined): boolean {
  return !worker?.stores?.length && !worker?.store_id;
}

/** True when a worker should be shown under the given store filter (no filter, store-less, or a match). */
export function workerMatchesStoreFilter(worker: WorkerDbModel | null | undefined, storeIds: number[] | null | undefined): boolean {
  if (!storeIds?.length) {
    return true;
  }
  if (workerHasNoStore(worker)) {
    return true;
  }
  return storeIds.some(storeId => workerServesStore(worker, storeId));
}

/**
 * The worker's primary store from their pivot assignments (is_primary, else the
 * first by position). Exceptional availabilities are store-OWNED on the backend
 * (no global bucket), so store-less contexts default to this.
 */
export function primaryStoreId(assignments: StoreWorkerAssignment[] | null | undefined): number | null {
  if (!assignments?.length) {
    return null;
  }
  return assignments.find(assignment => assignment.is_primary)?.store_id ?? assignments[0].store_id;
}

/**
 * The store a write must carry. An explicit scope (the active pill, or the store the
 * caller opened the dialog with) wins, then the worker's primary assignment, then the
 * worker's own default store. The last step is what keeps addon-off payloads explicit:
 * no assignments are loaded there, but every row still names the store it lands on
 * instead of relying on the server-side stamp.
 */
export function writeStoreId(
  explicitStoreId: number | null | undefined,
  assignments: StoreWorkerAssignment[] | null | undefined,
  worker: WorkerDbModel | null | undefined,
): number | null {
  // legacy workers can carry store_id = 0 — never a real store id; `||` folds it
  // to null so the key is omitted and the server treats the row as store-less,
  // exactly as before this resolver existed (`empty(0)` skips the stamp there too)
  return explicitStoreId ?? primaryStoreId(assignments) ?? (worker?.store_id || null);
}

/**
 * Strict scope filter for the availability tables (store-owned model):
 * every row belongs to exactly one store bucket. Store-less legacy rows
 * (store_id null from the API) render under the worker's primary store pill.
 */
export function scopeAvailabilities(
  rows: AvailabilityDbModel[],
  selectedStoreId: number | null,
  workerPrimaryStoreId: number | null,
): AvailabilityDbModel[] {
  return rows.filter(row => (row.store_id ?? workerPrimaryStoreId) === selectedStoreId);
}

/**
 * Pill partition for a worker's absence list, shared by the worker Absences tab and the
 * absences page worker view so the two can never drift. Store-owned model: every row
 * belongs to exactly one store pill — its own store_id. Store-less rows (company-wide
 * closures + legacy worker rows without a store) render under the worker's primary
 * store pill. Service-scoped rows never belong to a worker view. Sorted by from_date
 * like the server.
 */
export function scopeWorkerAbsences(
  rows: AbsenceDbModel[],
  workerId: number,
  selectedStoreId: number | null,
  workerPrimaryStoreId: number | null,
): AbsenceDbModel[] {
  return rows
    .filter(row => {
      if (row.appointment_service_id) {
        return false;
      }
      if (row.worker_id && row.worker_id !== workerId) {
        return false;
      }
      return (row.store_id ?? workerPrimaryStoreId) === selectedStoreId;
    })
    .sort((a, b) => String(a.from_date).localeCompare(String(b.from_date)));
}

/**
 * Every store id reachable from `rootIds` by walking `parent_id` downward, to unlimited
 * depth — the FE mirror of `StoreHierarchyService::expandDescendants()`, computed client-side
 * from the store list the API already returned. The **root ids themselves are excluded** from
 * the result (mirrors "descendant", not "visible set" — callers that want both write
 * `[...rootIds, ...descendantStoreIds(stores, rootIds)]`).
 *
 * Two tolerances that matter for correctness, both load-bearing:
 * - **A `parent_id` pointing at a store absent from `stores`** (the own-stores API response is
 *   pre-filtered, so a manager's own store may reference an invisible parent) never breaks the
 *   walk — such a store simply isn't reachable as anyone's descendant, which is correct; it does
 *   NOT cause other rows to be dropped.
 * - **A cycle in the data** (A→B→A) cannot loop forever — the `visited` set guards every id
 *   exactly once, so the walk always terminates on any graph, mirroring the backend's BFS
 *   (`plan.md` §5.3 — no recursive CTE, PHP/TS BFS with a visited set only).
 */
export function descendantStoreIds(
  stores: StoreDbModel[],
  rootIds: (number | null | undefined)[],
): number[] {
  const roots = new Set<number>(rootIds.filter((id): id is number => id != null));
  const visited = new Set<number>(roots);
  const descendants = new Set<number>();
  const queue: number[] = [...roots];

  while (queue.length) {
    const currentId = queue.shift();
    for (const store of stores) {
      if (store.parent_id != null && store.parent_id === currentId && store.id != null && !visited.has(store.id)) {
        visited.add(store.id);
        descendants.add(store.id);
        queue.push(store.id);
      }
    }
  }

  return Array.from(descendants);
}

/**
 * True when `candidateId` is `rootId` itself, or a descendant of it to any depth. The addon-off
 * shape (`hierarchyEnabled = false`) reduces to plain equality — byte-for-byte the pre-feature
 * check every store-scoped edit/delete gate used before this ticket.
 */
function isSelfOrDescendant(
  rootId: number | null | undefined,
  candidateId: number | null | undefined,
  stores: StoreDbModel[],
  hierarchyEnabled: boolean,
): boolean {
  if (rootId == null || candidateId == null) {
    return false;
  }
  if (rootId === candidateId) {
    return true;
  }
  if (!hierarchyEnabled) {
    return false;
  }
  return descendantStoreIds(stores, [rootId]).includes(candidateId);
}

/**
 * The FE mirror of cakephp's `getVisibleStoreIds()` — the set a store-scoped worker may act on.
 *
 * Deliberately WIDER than `workerCanActOnStore` below: this seeds from the worker's SERVED stores
 * (the `stores_workers` pivot when the multi-store add-on is on, plus the primary), because that is
 * what the backend's own `getVisibleStoreIds()` = `expandDescendants(getServedStoreIds(...))`
 * resolves. Scoping a picker to primary-plus-descendants alone drops a multi-store manager's
 * pivot-assigned stores, which the backend would have accepted.
 *
 * The primary is UNIONED in rather than used as a fallback, matching cake's `getServedStoreIds()`,
 * which appends `workers.store_id` whenever it is live and own-partner: a worker whose pivot
 * reconcile has not run — an Excel import, a clone that copied only `is_primary = false` rows, a
 * non-fatal `syncPrimaryStoresWorker()` early return — still serves their own store. `servedStoreIds`
 * returns `worker.stores` INSTEAD of the primary when `stores` is non-empty, so relying on it alone
 * dropped the primary in exactly that state. Liveness and ownership are not re-checked here because
 * every caller intersects the result with its own already-fetched, partner-scoped store list, so
 * this can never widen beyond what the API returned.
 *
 * Hierarchy add-on off → the served ids unchanged, no descendant walk.
 */
export function workerVisibleStoreIds(
  worker: StoreScopedWorker | null | undefined,
  stores: StoreDbModel[],
  hierarchyEnabled: boolean,
): number[] {
  const seeds = [...servedStoreIds(worker), worker?.store_id].filter((id): id is number => id != null);
  if (!seeds.length) {
    return [];
  }
  if (!hierarchyEnabled) {
    return [...new Set(seeds)];
  }
  return [...new Set([...seeds, ...descendantStoreIds(stores, seeds)])];
}

/**
 * The FE mirror of the store-item edit/delete gate: true when `storeId` is the worker's own
 * store, or — only when the hierarchy add-on is on for this partner — a descendant of it.
 * Mirrors `store-item.component.html`'s previous flat `store.id === loggedInWorker.store_id`
 * check, which is exactly what this reduces to when `hierarchyEnabled` is false.
 *
 * Fails closed on ambiguity by construction: a `null`/`undefined` worker, store id, or store_id
 * returns `false` — never renders an action the API would 403 on. Callers still gate this
 * BEHIND the coarse `edit:stores`/`delete:stores` (partner-wide) permission check first; this
 * helper only covers the `edit:own:stores`/`delete:own:stores` (own-store) branch.
 */
export function workerCanActOnStore(
  worker: WorkerDbModel | null | undefined,
  storeId: number | null | undefined,
  stores: StoreDbModel[],
  hierarchyEnabled: boolean,
): boolean {
  return isSelfOrDescendant(worker?.store_id, storeId, stores ?? [], hierarchyEnabled);
}

export interface StoreHierarchyRow {
  store: StoreDbModel;
  indentLevel: number;
  /**
   * The parent's full store record (not a plain display name) — the `child_of` badge is
   * multi-language aware (`is_multi_language` / `_translations`) exactly like the store's own
   * name (`store-item.component.html`'s `store | translation: 'name': lang`), and a raw
   * `store.name` would render the untranslated name for a multi-language parent. Translate at
   * render time in the template, same as every other store name in this feature.
   */
  parentStore: StoreDbModel | null;
}

/**
 * Orders `stores` so every child follows its parent (one level of indent per generation) and
 * attaches the parent store record for the `child_of` badge — the duller, safer alternative to
 * a tree component (no tree component exists in this app, and the drag-ordered `position` field
 * is a single global integer that renders nonsensically as a tree; `plan.md` §6 P7).
 *
 * Deliberately operates on whatever slice the caller passes in (e.g. one country's group in
 * `stores-list.component.ts`, which groups by country before rendering) — hierarchy ordering
 * therefore only reorders WITHIN that slice; a parent/child pair split across two slices each
 * render as their own root, which is the safe degrade for an edge case the ticket does not ask
 * this list to solve.
 *
 * Same two tolerances as `descendantStoreIds`: a `parent_id` outside the given slice renders that
 * row as a root (never dropped), and a cycle cannot loop forever (`visited` guards every id
 * exactly once, and any store left unvisited after the root walk is still emitted, as a root, in
 * its original relative position).
 */
export function orderStoresByHierarchy(stores: StoreDbModel[]): StoreHierarchyRow[] {
  const idsInSlice = new Set<number>(stores.filter(store => store.id != null).map(store => store.id));
  const childrenByParent = new Map<number, StoreDbModel[]>();
  const roots: StoreDbModel[] = [];

  for (const store of stores) {
    const parentId = store.parent_id;
    if (parentId != null && idsInSlice.has(parentId)) {
      const siblings = childrenByParent.get(parentId) ?? [];
      siblings.push(store);
      childrenByParent.set(parentId, siblings);
    } else {
      roots.push(store);
    }
  }

  const rows: StoreHierarchyRow[] = [];
  const visited = new Set<number>();

  const walk = (store: StoreDbModel, level: number, parentStore: StoreDbModel | null): void => {
    if (store.id != null) {
      if (visited.has(store.id)) {
        return;
      }
      visited.add(store.id);
    }
    rows.push({ store, indentLevel: level, parentStore });
    const children = (store.id != null ? childrenByParent.get(store.id) : undefined) ?? [];
    for (const child of children) {
      walk(child, level + 1, store);
    }
  };

  for (const root of roots) {
    walk(root, 0, null);
  }

  // Defensive: any store never reached from a root (only possible if every member of its
  // component is part of a cycle with no root) still renders — as a root, in original order —
  // rather than silently vanishing from the list.
  for (const store of stores) {
    if (store.id != null && !visited.has(store.id)) {
      walk(store, 0, null);
    }
  }

  return rows;
}

/**
 * One node of the store tree. Unlike `StoreHierarchyRow` (a flat pre-order list carrying an
 * indent number), this keeps real parent/child nesting, which is what a connector line and a
 * collapsible subtree need: the line is a single `border-left` on the children's container and
 * therefore stops at the last child by construction, at any depth. Drawing the same line from
 * a flat list needs per-row guide elements plus "am I the last sibling at this level" bookkeeping.
 */
export interface StoreTreeNode {
  store: StoreDbModel;
  children: StoreTreeNode[];
  depth: number;
  /**
   * ALL descendants, not just direct children — this is what the branch-count badge shows, and
   * the two differ as soon as the tree is three deep. A root with one child that itself has one
   * child reads "2". The delete dialog's count is deliberately a different number (direct
   * children only), so do not swap one for the other.
   */
  descendantCount: number;
}

/**
 * Builds the store tree for ONE slice of stores (the caller passes a single country group, since
 * hierarchy ordering runs within a country).
 *
 * Mirrors `orderStoresByHierarchy`'s containment rules exactly, so the two cannot drift:
 *  - a store whose `parent_id` is not present in this slice renders as a root, which is what
 *    keeps a parent/child pair split across two country groups visible in both halves;
 *  - a `visited` set terminates cycles instead of recursing forever;
 *  - any store never reached from a root (possible only inside a cycle with no root) is still
 *    emitted as a root rather than silently vanishing from the list.
 */
export function buildStoreTree(stores: StoreDbModel[]): StoreTreeNode[] {
  const idsInSlice = new Set<number>(stores.filter(store => store.id != null).map(store => store.id));
  const childrenByParent = new Map<number, StoreDbModel[]>();
  const roots: StoreDbModel[] = [];

  for (const store of stores) {
    const parentId = store.parent_id;
    if (parentId != null && idsInSlice.has(parentId)) {
      const siblings = childrenByParent.get(parentId) ?? [];
      siblings.push(store);
      childrenByParent.set(parentId, siblings);
    } else {
      roots.push(store);
    }
  }

  const visited = new Set<number>();

  const walk = (store: StoreDbModel, depth: number): StoreTreeNode | null => {
    if (store.id != null) {
      if (visited.has(store.id)) {
        return null;
      }
      visited.add(store.id);
    }
    const children = ((store.id != null ? childrenByParent.get(store.id) : undefined) ?? [])
      .map(child => walk(child, depth + 1))
      .filter((node): node is StoreTreeNode => node !== null);
    const descendantCount = children.reduce((sum, child) => sum + 1 + child.descendantCount, 0);
    return { store, children, depth, descendantCount };
  };

  const tree = roots
    .map(root => walk(root, 0))
    .filter((node): node is StoreTreeNode => node !== null);

  for (const store of stores) {
    if (store.id != null && !visited.has(store.id)) {
      const node = walk(store, 0);
      if (node) {
        tree.push(node);
      }
    }
  }

  return tree;
}

/**
 * May the caller act on this worker? The FE mirror of cakephp `workersShareStore(caller, target,
 * ..., includeDescendants: true)`: the caller always reaches itself; with the Store Hierarchy add-on
 * on (`hierarchyVisibleStoreIds` is a Set) the caller's visible stores (served + descendants) must
 * intersect the target's SERVED stores; with it off (`null`) the check is today's primary-store
 * equality OR a multi-store overlap.
 *
 * Asymmetric on purpose: only the caller side is expanded by descendants, so a child-store manager
 * never reaches a worker of an ancestor store. Fails closed on a null caller or target.
 */
export function callerCanActOnWorker(
  caller: StoreScopedWorker | null | undefined,
  target: StoreScopedWorker | null | undefined,
  hierarchyVisibleStoreIds: ReadonlySet<number> | null,
): boolean {
  if (!caller || !target) {
    return false;
  }
  if (caller.id != null && caller.id === target.id) {
    return true;
  }
  // the primary store counts as served on both sides, matching cake's getServedStoreIds() (servedStoreIds
  // returns stores[] instead of store_id when stores is non-empty)
  const targetStoreIds = servedStoreIdsWithPrimary(target);
  if (hierarchyVisibleStoreIds === null) {
    const callerStoreIds = servedStoreIdsWithPrimary(caller);
    return target.store_id === caller.store_id
      || workersShareStore(target, caller)
      || targetStoreIds.some(id => callerStoreIds.includes(id));
  }
  return targetStoreIds.some(id => hierarchyVisibleStoreIds.has(id));
}

/** The worker's served store ids with its primary store unioned in; only real ids (> 0). */
function servedStoreIdsWithPrimary(worker: StoreScopedWorker): number[] {
  return [...servedStoreIds(worker), worker.store_id].filter((id): id is number => id != null && id > 0);
}

/**
 * May the caller act on a record owned by `storeId` (absence, event, slot)? The FE mirror of
 * cakephp `workerServesStore(caller, storeId, ..., includeDescendants: true)`. Hierarchy off
 * (`null`): the caller's primary store or any store it serves. Hierarchy on: the store must be in
 * the caller's visible set. Fails closed on a null caller or store.
 */
export function callerCanActOnStoreId(
  caller: StoreScopedWorker | null | undefined,
  storeId: number | null | undefined,
  hierarchyVisibleStoreIds: ReadonlySet<number> | null,
): boolean {
  if (!caller) {
    return false;
  }
  if (storeId == null || storeId <= 0) {
    return false;
  }
  if (hierarchyVisibleStoreIds === null) {
    return storeId === caller.store_id || workerServesStore(caller, storeId);
  }
  return hierarchyVisibleStoreIds.has(storeId);
}
